import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

// --- Kept eager: everything above-the-fold on cold load ------------------
import Layout from "@/components/Layout";
import YayCelebration from "@/components/YayCelebration";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import PendingApproval from "@/pages/PendingApproval";

// --- Lazy: rarely visited or heavy pages ---------------------------------
// Splitting these out chops the main JS bundle by roughly a third — first
// paint is unchanged, secondary pages fetch on demand with the fallback
// shown below.
const ViewClients = lazy(() => import("@/pages/ViewClients"));
const AddClient = lazy(() => import("@/pages/AddClient"));
const ClientProfile = lazy(() => import("@/pages/ClientProfile"));
const EmailHistory = lazy(() => import("@/pages/EmailHistory"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Reports = lazy(() => import("@/pages/Reports"));
const CommissionsReport = lazy(() => import("@/pages/CommissionsReport"));
const ArchivedClients = lazy(() => import("@/pages/ArchivedClients"));
const DeletedClients = lazy(() => import("@/pages/DeletedClients"));
const Settings = lazy(() => import("@/pages/Settings"));
const RestrictedAccess = lazy(() => import("@/pages/RestrictedAccess"));

const PageFallback = () => (
  <div
    className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground"
    data-testid="page-suspense-fallback"
  >
    Loading…
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.active === false) return <PendingApproval />;
  return children;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/restricted" element={<RestrictedAccess />} />
        <Route element={<Protected><Layout /></Protected>}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/view" element={<ViewClients />} />
          <Route path="/add" element={<AddClient />} />
          <Route path="/clients/:id" element={<ClientProfile />} />
          <Route path="/emails" element={<EmailHistory />} />
          <Route path="/activity" element={<ActivityLog />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/reports/commissions" element={<CommissionsReport />} />
          <Route path="/archive" element={<ArchivedClients />} />
          <Route path="/trash" element={<DeletedClients />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster richColors position="top-right" />
          <YayCelebration />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
